import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type DynamicFilter,
  type FilterableAttribute,
  AttributeDataType,
} from "../types";

import { getOperatorsForDataType } from "../utils/operator-mapper";

import { ComboboxAsync } from "@/components/fields/combobox/combobox-async";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { DatePickerV2 } from "@/components/fields/date-picker";
import dayjs from "dayjs";

interface DynamicFilterRowProps {
  filter: DynamicFilter;
  attribute?: FilterableAttribute;
  availableAttributes: FilterableAttribute[];
  usedFields?: string[];
  onUpdate: (updates: Partial<DynamicFilter>) => void;
  onRemove: () => void;
  onChangeAttribute: (newAttribute: FilterableAttribute) => void;
  onForeignKeySearch?: (
    referenceTable: string,
    search: string,
    callback: (options: { label: string; value: string | number }[]) => void,
  ) => void;
}

export function DynamicFilterRow({
  filter,
  attribute,
  availableAttributes,
  usedFields = [],
  onUpdate,
  onRemove,
  onChangeAttribute,
}: DynamicFilterRowProps) {
  // Local state for input value to prevent re-renders on every keystroke
  const [localValue, setLocalValue] = useState<string>(filter.value ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local value when filter.value changes from outside (e.g., URL change)
  useEffect(() => {
    setLocalValue(filter.value ?? "");
  }, [filter.value]);

  // Debounced update to parent
  const debouncedUpdate = useCallback(
    (value: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        onUpdate({ value });
      }, 500);
    },
    [onUpdate],
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    debouncedUpdate(newValue);
  };

  const handleInputBlur = () => {
    // Immediately update on blur
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    onUpdate({ value: localValue });
  };

  const getAvailableOperators = () => {
    if (!attribute) {
      // Fallback to TEXT operators if no attribute
      return getOperatorsForDataType(AttributeDataType.TEXT);
    }
    return getOperatorsForDataType(attribute.dataType);
  };

  const availableOperators = getAvailableOperators();
  const isEmptyOperator = filter.type === "is empty";

  // Build selectable attributes: current attribute + unused attributes
  const selectableAttributes = useMemo(() => {
    const filtered = availableAttributes.filter(
      (attr) =>
        attr.field === filter.filterBy || !usedFields.includes(attr.field),
    );

    // If current field is set but not found in filtered, and we have attribute, ensure it's included
    if (
      filter.filterBy &&
      attribute &&
      !filtered.some((a) => a.field === filter.filterBy)
    ) {
      return [attribute, ...filtered];
    }

    return filtered.length > 0 ? filtered : availableAttributes;
  }, [availableAttributes, filter.filterBy, usedFields, attribute]);

  return (
    <div className="aero:flex aero:items-start aero:gap-2 aero:flex-wrap aero:sm:flex-nowrap aero:min-w-0">
      {/* Field select */}
      <Select
        value={filter.filterBy}
        onValueChange={(value) => {
          const newAttr = selectableAttributes.find((a) => a.field === value);

          if (newAttr) {
            onChangeAttribute(newAttr);
          }
        }}
      >
        <SelectTrigger className="aero:h-10 aero:flex-1 aero:min-w-[120px] aero:rounded aero:border-neutral-300">
          <SelectValue placeholder="Select Field" />
        </SelectTrigger>
        <SelectContent>
          {selectableAttributes.map((attr) => (
            <SelectItem key={attr.id} value={attr.field}>
              {attr.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filter.type}
        onValueChange={(value) => onUpdate({ type: value })}
      >
        <SelectTrigger className="aero:h-10 aero:flex-1 aero:min-w-[100px] aero:rounded aero:border-neutral-300">
          <SelectValue placeholder="Select operator" />
        </SelectTrigger>
        <SelectContent>
          {availableOperators.map((op) => (
            <SelectItem key={op.value} value={op.label.toLowerCase()}>
              {op.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* Value input - show different input based on data type and operator */}
      {!isEmptyOperator && (
        <>
          {attribute?.dataType === AttributeDataType.BOOLEAN ? (
            <div className="aero:flex aero:items-center aero:gap-3 aero:flex-1 aero:min-w-[150px] aero:h-10 aero:px-3 aero:rounded aero:border aero:border-neutral-300 aero:bg-white">
              <Switch
                checked={filter.value === true || filter.value === "true"}
                onCheckedChange={(checked) => {
                  onUpdate({ value: checked });
                }}
                id={`boolean-switch-${filter.filterBy}`}
              />
              <Label
                htmlFor={`boolean-switch-${filter.filterBy}`}
                className="aero:cursor-pointer aero:text-sm aero:font-medium aero:text-gray-700"
              >
                {filter.value === true || filter.value === "true"
                  ? "True"
                  : "False"}
              </Label>
            </div>
          ) : attribute?.dataType === AttributeDataType.FOREIGN_KEY &&
            attribute?.options ? (
            <div className="aero:flex-1 aero:min-w-[150px]">
              <ComboboxAsync
                options={attribute.options.map((opt) => ({
                  value: String(opt.value),
                  label: opt.label,
                }))}
                value={
                  Array.isArray(filter.value) && filter.value.length > 0
                    ? String(filter.value[0])
                    : String(filter.value || "")
                }
                onChange={(option) => {
                  // Convert single value to array format [value] for backend
                  const numericValue = Number(option.value);
                  onUpdate({
                    value: option.value ? [numericValue] : [],
                  });
                }}
                onSearchChange={() => {}}
                buttonPlaceholder="Select Option"
                inputPlaceholder="Search..."
                buttonClassName="aero:h-10 aero:w-full"
                clearable={true}
              />
            </div>
          ) : attribute?.dataType === AttributeDataType.DATETIME &&
            filter.type === "range" ? (
            <DatePickerV2
              range
              rangeValue={
                filter.value?.from && filter.value?.to
                  ? {
                      from: dayjs(filter.value.from).toDate(),
                      to: dayjs(filter.value.to).toDate(),
                    }
                  : null
              }
              onRangeChange={(range) => {
                if (range?.from && range?.to) {
                  onUpdate({
                    value: {
                      from: dayjs(range.from).format("YYYY-MM-DD"),
                      to: dayjs(range.to).format("YYYY-MM-DD"),
                    },
                  });
                } else {
                  onUpdate({ value: { from: "", to: "" } });
                }
              }}
              placeholder="Select date range"
              className="aero:flex-1 aero:min-w-[200px]"
              showTime={false}
            />
          ) : (
            <Input
              value={localValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder={
                attribute?.dataType === AttributeDataType.INT ||
                attribute?.dataType === AttributeDataType.FRACTION
                  ? "Enter number"
                  : attribute?.dataType === AttributeDataType.DATETIME
                    ? "Enter date"
                    : "Enter text"
              }
              type={
                attribute?.dataType === AttributeDataType.INT ||
                attribute?.dataType === AttributeDataType.FRACTION
                  ? "number"
                  : attribute?.dataType === AttributeDataType.DATETIME
                    ? "date"
                    : "text"
              }
              className="aero:h-10 aero:flex-1 aero:min-w-[150px] aero:rounded aero:border-neutral-300 aero:placeholder:italic"
            />
          )}
        </>
      )}
      <Button
        type="button"
        design="outline"
        size="iconMd"
        className="aero:h-10 aero:w-10 aero:rounded aero:border-[#911300] aero:text-[#911300] aero:hover:bg-red-50 aero:shrink-0 aero:p-2"
        onClick={onRemove}
      >
        <X className="aero:h-5 aero:w-5" />
      </Button>
    </div>
  );
}
